import { useEffect, useState, useRef } from 'react'
import { supabase } from '../../../../lib/supabase'

const CLUB_ID = 'a0000000-0000-0000-0000-000000000001'

const PRESET_POSITIONS = [
  'Chairman',
  'Vice Chairman',
  'Secretary General',
  'Treasurer',
  'Welfare Officer',
  'Public Relations Officer',
  'Organizing Secretary',
  'Youth Representative',
  'Women Representative',
  'Legal Advisor',
]

function getInitials(name) {
  return name?.split(' ').filter(Boolean).map((n) => n[0]).join('').toUpperCase().slice(0, 2) || '?'
}

// ── Photo upload button ───────────────────────────────────────────────────────
function PhotoUpload({ execId, currentUrl, onUploaded }) {
  const [uploading, setUploading] = useState(false)
  const inputRef = useRef(null)

  const handleUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (!file.type.startsWith('image/')) return
    if (file.size > 2 * 1024 * 1024) { alert('Image must be under 2MB'); return }

    setUploading(true)
    const fileExt = file.name.split('.').pop()
    const fileName = `${execId}.${fileExt}`

    const { error: uploadError } = await supabase.storage
      .from('executive-photos')
      .upload(fileName, file, { upsert: true })

    if (uploadError) { setUploading(false); return }

    const { data: { publicUrl } } = supabase.storage
      .from('executive-photos')
      .getPublicUrl(fileName)

    // Update photo_url in DB
    await supabase.from('executives').update({ photo_url: publicUrl }).eq('id', execId)
    setUploading(false)
    onUploaded(publicUrl)
  }

  return (
    <div className="flex flex-col items-center gap-2">
      {/* Avatar preview */}
      <div className="w-14 h-14 rounded-full bg-green-900/50 border-2 border-green-700/40 overflow-hidden flex items-center justify-center shrink-0">
        {currentUrl
          ? <img src={currentUrl} alt="Executive" className="w-full h-full object-cover" />
          : <span className="text-white text-xs font-black text-center px-1">Photo</span>
        }
      </div>
      <label className="bg-gray-200 dark:bg-white/10 hover:bg-gray-300 dark:hover:bg-white/20 text-gray-700 dark:text-white px-3 py-1.5 text-xs font-bold uppercase tracking-widest cursor-pointer transition-colors whitespace-nowrap">
        {uploading ? 'Uploading...' : currentUrl ? 'Change' : 'Upload'}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleUpload}
          disabled={uploading}
          className="hidden"
        />
      </label>
    </div>
  )
}

// ── Single editable executive row ─────────────────────────────────────────────
function ExecutiveRow({ exec, index, onSave, onDelete }) {
  const [editing, setEditing] = useState(false)
  const [formData, setFormData] = useState({
    name: exec.name,
    position: exec.position,
    sort_order: exec.sort_order,
  })
  const [photoUrl, setPhotoUrl] = useState(exec.photo_url || null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    const { error } = await supabase.from('executives').update({
      name: formData.name.trim(),
      position: formData.position.trim(),
      sort_order: Number(formData.sort_order),
    }).eq('id', exec.id)
    if (error) { setError(error.message); setSaving(false); return }
    setSaving(false)
    setEditing(false)
    onSave()
  }

  const handleCancel = () => {
    setFormData({ name: exec.name, position: exec.position, sort_order: exec.sort_order })
    setEditing(false)
    setError(null)
  }

  const inputClass = "bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-3 py-2 text-sm outline-none transition-colors w-full"

  return (
    <div className={`border transition-colors ${editing ? 'border-green-500/50 bg-green-50 dark:bg-green-900/10' : 'border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5'}`}>

      {/* Read view */}
      {!editing && (
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="w-10 h-10 rounded-full bg-green-900/50 border border-green-700/40 overflow-hidden flex items-center justify-center shrink-0">
            {photoUrl
              ? <img src={photoUrl} alt={exec.name} className="w-full h-full object-cover" />
              : <span className="text-white text-xs font-black">{getInitials(exec.name)}</span>
            }
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-black text-sm text-gray-900 dark:text-white uppercase tracking-tight truncate">
              {exec.name}
            </div>
            <div className="text-xs text-green-600 dark:text-green-400 uppercase tracking-widest font-semibold flex items-center gap-2">
              {exec.position}
              {index === 0 && <span className="bg-green-600 text-white px-1.5 py-0.5 text-xs font-bold">Chair</span>}
              {index === 1 && <span className="border border-green-700/50 text-green-400 px-1.5 py-0.5 text-xs font-bold">Vice</span>}
            </div>
          </div>
          <div className="hidden sm:block text-xs text-gray-400 shrink-0">#{index + 1}</div>
          <div className="flex items-center gap-3 shrink-0">
            <button onClick={() => setEditing(true)} className="text-xs text-green-600 dark:text-green-400 hover:text-green-700 font-bold uppercase tracking-widest transition-colors">
              Edit
            </button>
            <span className="text-gray-300 dark:text-white/20">|</span>
            <button onClick={() => onDelete(exec)} className="text-xs text-red-500 hover:text-red-400 font-bold uppercase tracking-widest transition-colors">
              Remove
            </button>
          </div>
        </div>
      )}

      {/* Edit view */}
      {editing && (
        <div className="px-4 py-4 flex flex-col gap-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-3 py-2 text-xs">{error}</div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 items-start">
            {/* Photo upload */}
            <PhotoUpload
              execId={exec.id}
              currentUrl={photoUrl}
              onUploaded={(url) => { setPhotoUrl(url); onSave() }}
            />

            {/* Fields */}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Full name"
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Position</label>
                <input
                  type="text"
                  list={`positions-${exec.id}`}
                  value={formData.position}
                  onChange={e => setFormData({ ...formData, position: e.target.value })}
                  placeholder="Select or type"
                  className={inputClass}
                />
                <datalist id={`positions-${exec.id}`}>
                  {PRESET_POSITIONS.map(p => <option key={p} value={p} />)}
                </datalist>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Display Order</label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={e => setFormData({ ...formData, sort_order: e.target.value })}
                  min="0"
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={handleCancel} className="border border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/30 text-gray-500 dark:text-gray-400 px-5 py-2 text-xs font-bold uppercase tracking-widest transition-colors">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700 disabled:bg-green-900 text-white px-5 py-2 text-xs font-bold uppercase tracking-widest transition-colors">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Add new executive row ─────────────────────────────────────────────────────
function AddRow({ nextOrder, onAdd }) {
  const [open, setOpen] = useState(false)
  const [formData, setFormData] = useState({ name: '', position: '', sort_order: nextOrder })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const handleAdd = async () => {
    if (!formData.name.trim() || !formData.position.trim()) { setError('Name and position are required'); return }
    setSaving(true); setError(null)
    const { error } = await supabase.from('executives').insert({
      club_id: CLUB_ID,
      name: formData.name.trim(),
      position: formData.position.trim(),
      sort_order: Number(formData.sort_order),
    })
    if (error) { setError(error.message); setSaving(false); return }
    setFormData({ name: '', position: '', sort_order: nextOrder + 1 })
    setSaving(false)
    setOpen(false)
    onAdd()
  }

  const inputClass = "bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-3 py-2 text-sm outline-none transition-colors w-full"

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="w-full border border-dashed border-gray-300 dark:border-white/20 hover:border-green-600 dark:hover:border-green-700 text-gray-400 hover:text-green-600 dark:hover:text-green-400 py-3 text-xs font-bold uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        Add Executive
      </button>
    )
  }

  return (
    <div className="border border-green-500/50 bg-green-50 dark:bg-green-900/10 px-4 py-4 flex flex-col gap-3">
      <div className="text-xs text-green-700 dark:text-green-400 uppercase tracking-widest font-bold">New Executive</div>
      {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-3 py-2 text-xs">{error}</div>}
      <p className="text-xs text-gray-400">Fill in the details and save. You can upload a photo after adding.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Full Name</label>
          <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Full name" className={inputClass} />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Position</label>
          <input
            type="text"
            list="new-exec-positions"
            value={formData.position}
            onChange={e => setFormData({ ...formData, position: e.target.value })}
            placeholder="Select or type"
            className={inputClass}
          />
          <datalist id="new-exec-positions">
            {PRESET_POSITIONS.map(p => <option key={p} value={p} />)}
          </datalist>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Display Order</label>
          <input type="number" value={formData.sort_order} onChange={e => setFormData({ ...formData, sort_order: e.target.value })} min="0" className={inputClass} />
        </div>
      </div>
      <div className="flex gap-2 mt-1">
        <button onClick={() => { setOpen(false); setError(null) }} className="border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 px-5 py-2 text-xs font-bold uppercase tracking-widest transition-colors">
          Cancel
        </button>
        <button onClick={handleAdd} disabled={saving} className="bg-green-600 hover:bg-green-700 disabled:bg-green-900 text-white px-5 py-2 text-xs font-bold uppercase tracking-widest transition-colors">
          {saving ? 'Adding...' : 'Add'}
        </button>
      </div>
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function ExecutivesForm() {
  const [executives, setExecutives] = useState([])
  const [loading, setLoading] = useState(true)

  const fetchExecutives = async () => {
    const { data } = await supabase
      .from('executives')
      .select('*')
      .eq('club_id', CLUB_ID)
      .order('sort_order', { ascending: true })
    if (data) setExecutives(data)
    setLoading(false)
  }

  useEffect(() => { fetchExecutives() }, [])

  const handleDelete = async (exec) => {
    if (!window.confirm(`Remove "${exec.name}" from the executives list?`)) return
    await supabase.from('executives').delete().eq('id', exec.id)
    fetchExecutives()
  }

  return (
    <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-5 sm:p-8">
      <div className="mb-6">
        <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Executives</div>
        <p className="text-gray-400 text-xs mt-1">
          These appear on the home page. Click Edit on any row to update their name, position, or photo. Changes are live immediately.
        </p>
      </div>

      {loading ? (
        <div className="text-gray-400 text-sm py-6 text-center">Loading...</div>
      ) : (
        <div className="flex flex-col gap-2">
          {executives.length === 0 && (
            <div className="text-gray-400 text-sm py-6 text-center border border-dashed border-gray-200 dark:border-white/10">
              No executives yet. Add the first one below.
            </div>
          )}
          {executives.map((exec, index) => (
            <ExecutiveRow
              key={exec.id}
              exec={exec}
              index={index}
              onSave={fetchExecutives}
              onDelete={handleDelete}
            />
          ))}
          <AddRow nextOrder={executives.length} onAdd={fetchExecutives} />
        </div>
      )}
    </div>
  )
}